import { Flex, Select, Text } from '@mantine/core'
import { createFormContext, zodResolver } from '@mantine/form'
import { useEffect, type HTMLAttributes } from 'react'

import { useMutationDeleteItemCategory } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreItemCategory, type ParameterID } from '@flecto/types'
import { useNotifications } from '@flecto/ui'

import { useFlattenItemCategories } from '../../hooks/useFlattenItemCategories'
import { useCategoryDeleteFormSchema } from './useCategoryDeleteFormSchema'
import { useSelectCategoryOptions } from './useSelectCategoryOptions'

export const [
  CategoryDeleteFormProvider,
  useCategoryDeleteFormContext,
  useFormCategoryDelete,
] = createFormContext<{ new_category_id?: ParameterID }>()

type CategoryDeleteFormProps = HTMLAttributes<HTMLFormElement> & {
  category?: CoreItemCategory
  onSuccess?: () => void
  onError?: () => void
  onValidationChange?: (isValid: boolean) => void
  onLoading?: (loading: boolean) => void
}

export function CategoryDeleteForm({
  category,
  onSuccess,
  onError,
  onValidationChange,
  onLoading,
  ...props
}: CategoryDeleteFormProps) {
  const { categories } = useFlattenItemCategories() || []
  const { showNotification } = useNotifications()
  const { mutateAsync: deleteCategory } = useMutationDeleteItemCategory()
  const selectCategories = useSelectCategoryOptions({ categories }).filter(
    (item) => Number(item.value) !== category?.internal_id
  )
  const { t } = useTranslation('saas', {
    keyPrefix: 'profile_inventory',
  })

  const { schema, initialValues } = useCategoryDeleteFormSchema({
    payload: {},
  })

  const form = useFormCategoryDelete({
    initialValues,
    validate: zodResolver(schema),
  })

  async function deleteItemCategory() {
    try {
      if (category?.internal_id && form.values.new_category_id) {
        onLoading?.(true)
        await deleteCategory({
          categoryId: category?.internal_id,
          newCategoryId: Number(form.values.new_category_id),
        })
        showNotification({
          type: 'success',
          message: t('notification_delete_category_success'),
        })
      }
      onSuccess?.()
    } catch (_) {
      showNotification({
        type: 'error',
        message: t('notification_delete_category_error'),
      })
      onError?.()
    } finally {
      onLoading?.(false)
    }
  }

  useEffect(() => {
    const isValid = Boolean(form.values.new_category_id)
    onValidationChange?.(isValid)
  }, [form.values.new_category_id, onValidationChange])

  return (
    <CategoryDeleteFormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => deleteItemCategory())}>
        <Flex direction='column' gap='xl' mt={'md'}>
          <Flex direction='column' gap='sm'>
            <Text fw={500}>
              {t('category_delete_modal.select', {
                count: category?.items_count,
              })}
            </Text>
            <Select
              mt='sm'
              size='md'
              placeholder={t('category_delete_modal.select_placeholder')}
              data={selectCategories}
              searchable
              onChange={(value) => {
                form.setFieldValue('new_category_id', value || undefined)
              }}
            />
          </Flex>
        </Flex>
      </form>
    </CategoryDeleteFormProvider>
  )
}
